import { Box, Divider, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { isValidSharePointUrl, sanitizeInput } from '../../utils/formUtils';

import WizardNavigation from './WizardNavigation';

interface Props {
  onNext: (data: { siteUrl: string }) => void;
  onBack: () => void;
  activeStep: number;
  credentials: {
    siteUrl: string;
  };
  onBackToAccount?: () => void;
}

const Step1CreateSite: React.FC<Props> = ({
  onNext,
  onBack,
  activeStep,
  credentials,
  onBackToAccount,
}) => {
  const [siteUrl, setSiteUrl] = useState(credentials.siteUrl || '');
  const [siteUrlError, setSiteUrlError] = useState('');

  const handleSiteUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value);
    setSiteUrl(sanitizedValue);
    if (siteUrlError) {
      validateSiteUrl(sanitizedValue);
    }
  };

  const validateSiteUrl = (value: string) => {
    if (!value) {
      setSiteUrlError('SharePoint URL is required.');
      return false;
    }
    if (!isValidSharePointUrl(value)) {
      setSiteUrlError(
        'Please enter a valid SharePoint URL (e.g., https://yourco.sharepoint.com/sites/...).'
      );
      return false;
    }
    setSiteUrlError('');
    return true;
  };

  const handleNext = () => {
    if (validateSiteUrl(siteUrl)) {
      onNext({ siteUrl });
    }
  };

  return (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h6" gutterBottom>
        Step 1: Create a SharePoint Site Dedicated to BIDLOGIQ
      </Typography>
      <List dense sx={{ textAlign: 'left' }}>
        <ListItem>
          <ListItemText primary={<span>1. Go directly to your SharePoint home page:</span>} />
        </ListItem>
        <ListItem>
          <Box component="code" sx={{ fontSize: '0.9rem', pl: 2, display: 'block' }}>
            https://YOURCOMPANY.sharepoint.com
          </Box>
        </ListItem>
        <ListItem>
          <ListItemText primary='2. Click "Create site"' />
        </ListItem>
        <ListItem>
          <ListItemText primary='3. Choose "Team site"' />
        </ListItem>
        <ListItem>
          <ListItemText primary='4. Choose "Standard Team"' />
        </ListItem>
        <ListItem>
          <ListItemText primary='5. Click "Use Template"' />
        </ListItem>
        <ListItem>
          <ListItemText primary="6. Fill in the site details:" />
        </ListItem>
        <ListItem sx={{ pl: 4 }}>
          <ListItemText primary='– Site name: "BIDLOGIQ SharePoint Integration, Click Next"' />
        </ListItem>
        <ListItem sx={{ pl: 4 }}>
          <ListItemText primary='– Privacy: Select "Private, Click Create Site"' />
        </ListItem>
        <ListItem sx={{ pl: 4 }}>
          <ListItemText
            primary={
              <span>
                – <b>Important:</b> Don't add any users! The BIDLOGIQ app will handle all user
                interactions with file access to the SharePoint.
              </span>
            }
          />
        </ListItem>
        <ListItem>
          <ListItemText primary='7. Click "Finish" after it is done setting up' />
        </ListItem>
        <ListItem>
          <ListItemText
            primary={
              <span>
                8. <b>Important:</b> Copy the full URL of your new site and paste it below.
              </span>
            }
          />
        </ListItem>
      </List>
      <TextField
        label="Full SharePoint Site URL"
        value={siteUrl}
        onChange={handleSiteUrlChange}
        onBlur={() => validateSiteUrl(siteUrl)}
        fullWidth
        variant="outlined"
        margin="normal"
        placeholder="https://YOUR-TENANT.sharepoint.com/sites/YOUR-SITE-NAME"
        error={!!siteUrlError}
        helperText={siteUrlError}
        required
      />
      <Divider sx={{ my: 3 }} />
      <WizardNavigation
        activeStep={activeStep}
        onBack={onBack}
        onNext={handleNext}
        isNextDisabled={!siteUrl || !!siteUrlError}
        {...(onBackToAccount && { onBackToAccount })}
      />
    </Box>
  );
};

export default Step1CreateSite;
